import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { chatApi } from '@data/rest/api';
import { AgentType, Chat } from '@common/api';
import toast from 'react-hot-toast';
import { useI18nContext } from '@i18n/i18n-react';
import { DEFAULT_AGENT_TYPE } from '../agentTypes';

export const CHATS_KEY = 'chats';

/**
 * Declarative query for fetching all chats for the current user.
 * Automatically handles caching, loading states, and refetching.
 */
export function useChats(enabled: boolean = true) {
    const { LL } = useI18nContext();
    return useQuery({
        queryKey: [CHATS_KEY],
        queryFn: async (): Promise<Chat[]> => {
            try {
                return await chatApi.findAllByUserId();
            } catch {
                toast.error(LL.CHAT_FAILED_LOAD());
                return [];
            }
        },
        enabled,
        staleTime: 0, // Always refetch to get latest chats
    });
}

/**
 * Hook for chat CRUD operations with automatic cache invalidation.
 */
export function useChatMutations(hideModal: () => void) {
    const queryClient = useQueryClient();
    const { LL } = useI18nContext();

    const createChatMutation = useMutation({
        mutationFn: async (agentType: AgentType = DEFAULT_AGENT_TYPE): Promise<number> => {
            const { chatId } = await chatApi.create({ agentType });
            return chatId;
        },
        onSuccess: () => {
            queryClient.invalidateQueries([CHATS_KEY]);
            toast.success(LL.CHAT_CREATED_NEW());
        },
        onError: () => {
            toast.error(LL.CHAT_FAILED_CREATE());
        },
    });

    const renameChatMutation = useMutation({
        mutationFn: async ({ chatId, newName }: { chatId: number; newName: string }) => {
            await chatApi.updateName({ chatId, name: newName });
        },
        onSuccess: () => {
            queryClient.invalidateQueries([CHATS_KEY]);
            toast.success(LL.CHAT_RENAMED());
            hideModal();
        },
        onError: () => {
            toast.error(LL.CHAT_FAILED_RENAME());
        },
    });

    const deleteChatMutation = useMutation({
        mutationFn: async (chatId: number) => {
            await chatApi._delete({ chatId });
        },
        onSuccess: () => {
            queryClient.invalidateQueries([CHATS_KEY]);
            toast.success(LL.CHAT_DELETED());
        },
        onError: () => {
            toast.error(LL.CHAT_FAILED_DELETE());
        },
    });

    return {
        createChat: createChatMutation.mutateAsync,
        renameChat: renameChatMutation.mutateAsync,
        deleteChat: deleteChatMutation.mutateAsync,
        isCreatingChat: createChatMutation.isLoading,
        isRenamingChat: renameChatMutation.isLoading,
        isDeletingChat: deleteChatMutation.isLoading,
    };
}
